import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { environment } from "@environments/environments";
import { DuplicateQuoteRequest } from "@office/quotes/quotes.types";
import { ConvertToInvoiceWithDeliveryJobRequest, ConvertToInvoiceWithDeliveryJobResult, QuoteDraftData } from "@shared/models/domain/custom-views.model";
import { InvoiceSummaryView, QuoteLinePickupItemRateView, QuoteSummaryView, QuoteView } from "@shared/models/domain/domain-view.model";
import { Address, Job, Quote } from "@shared/models/domain/domain.model";
import { Invoice } from "@shared/models/domain/wms.model";
import { PageRequest } from "@shared/models/request/page-request.model";
import { PaymentRequest } from "@shared/models/request/payment-request.model";
import { QuotePaymentRequest } from "@shared/models/request/quote-payment-request.model";
import { PageResponse } from "@shared/models/response/page-response.model";
import { CoreApiService } from "@shared/services/common/core-api.service";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, throwError } from "rxjs";

@Injectable({
    providedIn: 'root',
})
export class OfficeQuotesService extends CoreApiService<QuoteView> {
    private _httpClient: HttpClient;


    baseUrl = environment.apiUrl;
    private _quotes = new BehaviorSubject<PageResponse<QuoteView>>(null);
    private _invoice = new BehaviorSubject<Invoice>(null);
    private _quote = new BehaviorSubject<QuoteView>(null);
    private _quoteData = new BehaviorSubject<Quote>(null);

    draftQuote = signal<QuoteDraftData | null>(null);

    constructor() {
        const _httpClient = inject(HttpClient);

        super('admin/quotes');
    
        this._httpClient = _httpClient;
    }

    get quotes$(): Observable<PageResponse<QuoteView>> {
        return this._quotes.asObservable();
    }

    get invoice$(): Observable<Invoice> {
        return this._invoice.asObservable();
    }

    get quote$(): Observable<QuoteView> {
        return this._quote.asObservable();
    }

    get quoteData$(): Observable<Quote> {
        return this._quoteData.asObservable();
    }
    
    saveQuote(quote: Quote): Observable<QuoteView> {
        let task: Observable<PageResponse<QuoteView>>
        if (quote.id) {
            task = this._httpClient.put<PageResponse<QuoteView>>(this.baseUrl + 'api/admin/quotes/' + quote.id, quote);
        } else {
            task = this._httpClient.post<PageResponse<QuoteView>>(this.baseUrl + 'api/admin/quotes', quote);
        }

        return task
            .pipe(map((res: PageResponse<QuoteView>) => res.item))

    }

    getQuotes(request: PageRequest): Observable<PageResponse<QuoteView>> {
        const pageRequest = new PageRequest().deserialize(request ?? {});
        return this._httpClient.get<PageResponse<QuoteView>>(this.baseUrl + 'api/admin/view/quotes', { params: pageRequest.toParams() })
            .pipe(
                map((res: PageResponse<QuoteView>) => {
                    this._quotes.next(res ?? null);
                    return res ?? null;
                }));
    }

    getQuoteDataById(quoteId): Observable<Quote> {
        return this._httpClient.get<PageResponse<Quote>>(this.baseUrl + 'api/admin/quotes/' + quoteId)
            .pipe(
                map((res) => {
                    if (res.item) {
                        const ret = res.item;
                        this._quoteData.next(ret);
                        return ret;
                    }
                    return null;
                }));
    }

    initQuote(quote: Invoice): Observable<Invoice> {
        quote.status = "QUOTE.PENDING";
        return this._httpClient.post<PageResponse<Invoice>>(this.baseUrl + 'api/admin/invoice', quote)
            .pipe(
                map((res) => {
                    if (res.item) {
                        const ret = res.item;
                        this._invoice.next(ret);
                        return ret;
                    }
                    return null;
                }), catchError((err: HttpErrorResponse = null) => {
                    return throwError(() => err);
                }));
    }

    getAddressByExternalId(address: Address): Observable<Address> {
        const q = {
            "find[externalPlaceId]": address.externalPlaceId
            , currentPage: 1
            , itemsPerPage: 1
        };

        return this._httpClient.get<PageResponse<Address>>(`${this.baseUrl}api/admin/address`, { params: q })
            .pipe(
                switchMap((data) => {
                    if (data.items?.length && data.items[0].addressId) {
                        return of(data.items[0]);
                    }

                    return this._httpClient.post<PageResponse<Address>>(`${this.baseUrl}api/admin/address`, address)
                        .pipe(map((data2) => {
                            if (data2?.item?.addressId) {
                                return data2.item;
                            }
                            return null;
                        }))
                }))
    }

    getAddressById(addressId: number): Observable<Address> {
        return this._httpClient.get<PageResponse<Address>>(`${this.baseUrl}api/admin/address/` + addressId).pipe(
            map((res) => {
                if (res?.item) {
                    return res.item;
                }
            })
        );
    }

    resendReminder(quoteId: string) {
        return this._httpClient.post<QuoteView>(`${this.baseUrl}api/admin/quotes/resend/reminder/${quoteId}`, {})
            .pipe(
                map(() => true),
            );
    }

    duplicateQuote(lineItemData: DuplicateQuoteRequest): Observable<Quote> {
        return this._httpClient.post<PageResponse<Quote>>(`${this.baseUrl}api/admin/quotes/duplicate/${lineItemData?.duplicateQuoteId}`, lineItemData, { params: {} })
            .pipe(map((res: PageResponse<Quote>) => res?.item));
    }

    convertQuoteToInvoiceWithDeliveryJob(quoteId: string, request: ConvertToInvoiceWithDeliveryJobRequest): Observable<ConvertToInvoiceWithDeliveryJobResult> {
        return this._httpClient.post<PageResponse<ConvertToInvoiceWithDeliveryJobResult>>(`${this.baseUrl}api/admin/quotes/convert-to-invoice-with-delivery-job/${quoteId}`, request).pipe(
            map((res: PageResponse<ConvertToInvoiceWithDeliveryJobResult>) => res.item)
        );
    }
    
    convertQuoteToInvoice(quoteId: string): Observable<Quote> {
        return this._httpClient.post<PageResponse<Quote>>(`${this.baseUrl}api/admin/quotes/convert-to-invoice/${quoteId}`, {}).pipe(
            map((res: PageResponse<Quote>) => res.item)
        );
    }

    createFirstMilePickupDeliveryJob(quoteId: string, paymentRequest: PaymentRequest): Observable<Job> {
        return this._httpClient.post<PageResponse<Job>>(`${this.baseUrl}api/admin/quotes/create-first-mile-pickup-delivery-job/${quoteId}`, paymentRequest).pipe(
            map((res: PageResponse<Job>) => res.item)
        );
    }

    getLatestNumber(type): Observable<PageResponse<string>> {
        const task: Observable<PageResponse<string>> = this._httpClient.get<PageResponse<string>>(`${this.baseUrl}api/admin/quotes/get-latest-${type === 1 ? 'quote' : 'invoice'}-number`, {})
        return task;
    }
    activateQuote(invoiceId: string) {
        return this._httpClient.put<PageResponse<Quote>>(`${this.baseUrl}api/admin/quotes/activate/${invoiceId}`, {}, { params: {} })
            .pipe(
                map((data) => {
                    return data.item;
                }),
            );
    }

    completeInvoice(request: QuotePaymentRequest): Observable<boolean> {
        return this._httpClient.post<boolean>(`${this.baseUrl}api/admin/quotes/${request.quoteId}/complete`, request);
    }

    createRepairQuote(storageItemInstanceId:string): Observable<Quote> {
        return this._httpClient.post<PageResponse<Quote>>(`${this.baseUrl}api/admin/quotes/create-repair-quote/${storageItemInstanceId}`,{}).pipe(
            map((res: PageResponse<Quote>) => {
                if (res?.item) return res.item;
            })
        );
    }

    getMetropolitanPricing(quoteId: string, quoteLineItemId: string): Observable<PageResponse<QuoteLinePickupItemRateView[]>> {
        return this._httpClient.post<PageResponse<QuoteLinePickupItemRateView[]>>(`${this.baseUrl}api/admin/quotes/${quoteId}/quoteLineItem/${quoteLineItemId}/metropolitan-pricing`, {});
    }

    getQuoteSummary(websiteId?: number): Observable<QuoteSummaryView> {
        const request = new PageRequest();
        request.itemsPerPage = 1000;
        if (websiteId) {
            request.find['websiteId'] = websiteId;
        }

        return this._httpClient.get<PageResponse<QuoteSummaryView>>(`${this.baseUrl}api/admin/view/quote-summary`, { params: request.toParams() })
            .pipe(map((response: PageResponse<QuoteSummaryView>) => this.aggregateSummaryRows<QuoteSummaryView>(response?.items ?? [])));
    }

    getInvoiceSummary(websiteId?: number): Observable<InvoiceSummaryView> {
        const request = new PageRequest();
        request.itemsPerPage = 1000;
        if (websiteId) {
            request.find['websiteId'] = websiteId;
        }

        return this._httpClient.get<PageResponse<InvoiceSummaryView>>(`${this.baseUrl}api/admin/view/invoice-summary`, { params: request.toParams() })
            .pipe(map((response: PageResponse<InvoiceSummaryView>) => this.aggregateSummaryRows<InvoiceSummaryView>(response?.items ?? [])));
    }

    private aggregateSummaryRows<T extends QuoteSummaryView | InvoiceSummaryView>(rows: T[]): T {
        const oldestSentDate = rows.reduce((oldest: string | null, r) => {
            if (!r.oldestSentDate) return oldest;
            return (!oldest || r.oldestSentDate < oldest) ? r.oldestSentDate : oldest;
        }, null as string | null);

        return {
            openCount: rows.reduce((sum, r) => sum + (r.openCount || 0), 0),
            openValue: rows.reduce((sum, r) => sum + (r.openValue || 0), 0),
            draftCount: rows.reduce((sum, r) => sum + (r.draftCount || 0), 0),
            sentAwaitingCount: rows.reduce((sum, r) => sum + (r.sentAwaitingCount || 0), 0),
            oldestSentDate,
            last30DaysTotal: rows.reduce((sum, r) => sum + (r.last30DaysTotal || 0), 0),
            last30DaysCount: rows.reduce((sum, r) => sum + (r.last30DaysCount || 0), 0),
            warehouseId: null,
        } as T;
    }
}
